import { Badge, Button, Input, Select } from '@artosera/ui';
import { useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router';
import { useTranslation } from 'react-i18next';
import { Check, Circle, TriangleAlert } from 'lucide-react';
import type { Langue } from '@artosera/shared';
import {
  useCampagne,
  useListesNewsletter,
  useModifierCampagne,
  useReglagesNewsletter,
  type CampagneNewsletter,
} from '../../api/newsletter.js';
import { galerieALaction } from '../../auth/sujet.js';
import { useShellContext } from '../shell/context.js';
import { ApercuCampagne } from './ApercuCampagne.js';
import { BlocsEditeur } from './BlocsEditeur.js';
import { BoutonDupliquer } from './BoutonDupliquer.js';
import { EnvoiDialog } from './EnvoiDialog.js';
import { ErreurNewsletter } from './ErreurNewsletter.js';
import { Etapes, ETAPES, etapeDe, type Etape } from './Etapes.js';
import { RegleListe } from './RegleListe.js';
import { StatistiquesCampagne } from './StatistiquesCampagne.js';
import styles from './NewsletterPage.module.css';
export function CampagnePage() {
  const { id = '' } = useParams();
  const { galerieSlug } = useShellContext();
  const campagne = useCampagne(galerieSlug, id);
  const { t } = useTranslation('commun');
  if (campagne.isPending) return <p role="status">{t('chargement')}</p>;
  if (!campagne.data) return <ErreurNewsletter erreur={campagne.error} />;
  return (
    <FormCampagne
      key={`${id}:${campagne.data.campagne.revision}:${campagne.data.campagne.etat}`}
      initiale={campagne.data.campagne}
    />
  );
}
function FormCampagne({ initiale }: { initiale: CampagneNewsletter }) {
  const { t } = useTranslation(['regie', 'commun']);
  const { galerieSlug, galerie } = useShellContext();
  const [params, setParams] = useSearchParams();
  // Une campagne partie s'ouvre sur ses résultats, un brouillon sur son contenu.
  const etape = etapeDe(params.get('etape') ?? (initiale.etat === 'brouillon' ? null : 'envoi'));
  const [valeurs, setValeurs] = useState(initiale);
  const [envoiOuvert, setEnvoi] = useState(false);
  const [sale, setSale] = useState(false);
  const listes = useListesNewsletter(galerieSlug);
  const reglages = useReglagesNewsletter(galerieSlug);
  const modifier = useModifierCampagne(galerieSlug);
  const lectureSeule =
    initiale.etat !== 'brouillon' || !galerieALaction(galerie, 'newsletter:write');
  const changer = (patch: Partial<CampagneNewsletter>) => {
    setValeurs({ ...valeurs, ...patch });
    setSale(true);
  };
  const aller = (e: Etape) => setParams({ etape: e }, { replace: true });
  const destinataires =
    listes.data?.listes
      .filter((l) => valeurs.listes.includes(l.id))
      .reduce((n, l) => n + l.membres, 0) ?? 0;
  const connecteur = reglages.data?.reglages.connecteur;
  const expediteur = connecteur?.expediteur
    ? `${connecteur.expediteur.nom} <${connecteur.expediteur.email}>`
    : null;
  const contenuPret =
    Boolean(valeurs.titre.trim() && valeurs.sujet.trim()) &&
    valeurs.blocs.length > 0 &&
    valeurs.blocs.every((b) => b.type !== 'texte' || b.texte.trim());
  const destinatairesPrets = valeurs.listes.length > 0 && destinataires > 0;
  const peutEnvoyer = contenuPret && destinatairesPrets && !sale && initiale.etat === 'brouillon';
  const rang = ETAPES.indexOf(etape);
  const verifications: [string, boolean][] = [
    ['contenu', contenuPret],
    ['destinataires', destinatairesPrets],
    ['enregistre', !sale],
  ];
  return (
    <div className={styles.page} data-ui="newsletter-campaign">
      <Link to={`/${galerieSlug}/newsletter?onglet=campagnes`}>
        {t('commun:navigation.newsletter')}
      </Link>
      <header data-ui="page-header">
        <h1>{valeurs.titre}</h1>
        <Badge>{t(`regie:newsletter.etats.${initiale.etat}`)}</Badge>
        <BoutonDupliquer campagne={initiale} />
      </header>
      <Etapes
        courante={etape}
        onChoisir={aller}
        terminees={{
          contenu: contenuPret,
          destinataires: destinatairesPrets,
          envoi: initiale.etat !== 'brouillon',
        }}
      />
      <form
        className={styles.formulaire}
        onSubmit={(e) => {
          e.preventDefault();
          modifier.mutate({
            id: initiale.id,
            corps: {
              titre: valeurs.titre,
              sujet: valeurs.sujet,
              langue: valeurs.langue,
              listes: valeurs.listes,
              blocs: valeurs.blocs,
              revision: initiale.revision,
            },
          });
        }}
      >
        {etape === 'contenu' ? (
          <>
            <label>
              {t('regie:newsletter.campagne.titre')}
              <Input
                required
                maxLength={120}
                value={valeurs.titre}
                readOnly={lectureSeule}
                onChange={(e) => changer({ titre: e.target.value })}
              />
              <span className={styles.aide}>{t('regie:newsletter.campagne.titreAide')}</span>
            </label>
            <label>
              {t('regie:newsletter.campagne.sujet')}
              <Input
                required
                maxLength={200}
                value={valeurs.sujet}
                readOnly={lectureSeule}
                onChange={(e) => changer({ sujet: e.target.value })}
              />
              <span className={styles.aide}>
                {t('regie:newsletter.campagne.sujetAide', { n: valeurs.sujet.length })}
              </span>
            </label>
            <label htmlFor="langue-campagne">{t('regie:newsletter.campagne.langue')}</label>
            <Select
              id="langue-campagne"
              value={valeurs.langue}
              disabled={lectureSeule}
              options={[
                { value: 'fr', label: 'Français' },
                { value: 'en', label: 'English' },
              ]}
              onValueChange={(v) => changer({ langue: v as Langue })}
            />
            <BlocsEditeur
              blocs={valeurs.blocs}
              onChange={(blocs) => changer({ blocs })}
              lectureSeule={lectureSeule}
              langue={valeurs.langue}
            />
          </>
        ) : null}
        {etape === 'destinataires' ? (
          <fieldset disabled={lectureSeule}>
            <legend>{t('regie:newsletter.campagne.listes')}</legend>
            <p className={styles.aide}>{t('regie:newsletter.campagne.listesAide')}</p>
            {listes.data && !listes.data.listes.some((l) => !l.archive) ? (
              <p className={styles.vide}>
                {t('regie:newsletter.campagne.aucuneListe')}{' '}
                <Link to={`/${galerieSlug}/newsletter?onglet=listes`}>
                  {t('regie:newsletter.campagne.creerListe')}
                </Link>
              </p>
            ) : null}
            {listes.data?.listes
              .filter((l) => !l.archive)
              .map((l) => (
                <label className={styles.case} key={l.id}>
                  <input
                    type="checkbox"
                    checked={valeurs.listes.includes(l.id)}
                    onChange={(e) =>
                      changer({
                        listes: e.target.checked
                          ? [...valeurs.listes, l.id]
                          : valeurs.listes.filter((x) => x !== l.id),
                      })
                    }
                  />
                  <span>
                    {l.nom} · {t('regie:newsletter.listes.destinataires', { count: l.membres })}
                    {l.regle ? (
                      <span className={styles.aide}>
                        <RegleListe regle={l.regle} />
                      </span>
                    ) : null}
                  </span>
                </label>
              ))}
            <p>
              {t('regie:newsletter.campagne.destinatairesIndicatifs', { count: destinataires })}
            </p>
            <p className={styles.aide}>{t('regie:newsletter.campagne.consentement')}</p>
          </fieldset>
        ) : null}
        {etape === 'apercu' ? (
          <ApercuCampagne campagne={initiale} sujet={valeurs.sujet} expediteur={expediteur} />
        ) : null}
        {etape === 'envoi' ? (
          <section className={styles.section} aria-label={t('regie:newsletter.etapes.envoi')}>
            <h2>{t('regie:newsletter.envoi.titre')}</h2>
            {initiale.etat === 'brouillon' ? (
              <>
                <ul className={styles.verifications}>
                  {verifications.map(([cle, ok]) => (
                    <li key={cle} data-ok={ok ? 'oui' : 'non'}>
                      {ok ? (
                        <Check size={16} aria-hidden="true" />
                      ) : (
                        <Circle size={16} aria-hidden="true" />
                      )}
                      {t(`regie:newsletter.envoi.verifications.${cle}`)}
                      <span className={styles.masque}>
                        {' — '}
                        {t(ok ? 'regie:newsletter.envoi.fait' : 'regie:newsletter.envoi.aFaire')}
                      </span>
                    </li>
                  ))}
                </ul>
                <dl className={styles.recap}>
                  <div>
                    <dt>{t('regie:newsletter.apercu.objet')}</dt>
                    <dd>{valeurs.sujet}</dd>
                  </div>
                  <div>
                    <dt>{t('regie:newsletter.apercu.de')}</dt>
                    <dd>{expediteur ?? t('regie:newsletter.apercu.expediteurInconnu')}</dd>
                  </div>
                  <div>
                    <dt>{t('regie:newsletter.envoi.destinataires')}</dt>
                    <dd>{t('regie:newsletter.listes.destinataires', { count: destinataires })}</dd>
                  </div>
                  <div>
                    <dt>{t('regie:newsletter.envoi.quand')}</dt>
                    <dd>{t('regie:newsletter.envoi.immediat')}</dd>
                  </div>
                </dl>
                {connecteur && !connecteur.configure ? (
                  <p className={styles.alerte} role="status">
                    <TriangleAlert size={16} aria-hidden="true" />{' '}
                    {t('regie:newsletter.reglages.nonConfigureAide')}
                  </p>
                ) : null}
                {galerieALaction(galerie, 'newsletter:send') ? (
                  <div className={styles.actions}>
                    <Button
                      type="button"
                      variant="secondary"
                      disabled={!peutEnvoyer}
                      onClick={() => setEnvoi(true)}
                    >
                      {t('regie:newsletter.campagne.envoyer')}
                    </Button>
                    {!peutEnvoyer ? (
                      <span className={styles.aide}>{t('regie:newsletter.envoi.bloque')}</span>
                    ) : null}
                  </div>
                ) : (
                  <p className={styles.aide}>{t('regie:newsletter.envoi.sansDroit')}</p>
                )}
              </>
            ) : (
              <>
                {initiale.erreur ? (
                  <p role="alert">
                    {t(`regie:newsletter.campagne.erreurs.${initiale.erreur}`, {
                      defaultValue: t('regie:newsletter.campagne.erreurs.autre'),
                    })}
                  </p>
                ) : null}
                <p>{t(`regie:newsletter.envoi.etat.${initiale.etat}`)}</p>
                <StatistiquesCampagne campagne={initiale} />
              </>
            )}
          </section>
        ) : null}
        <ErreurNewsletter erreur={modifier.error} />
        {sale ? <p role="status">{t('regie:newsletter.campagne.enregistrerAvant')}</p> : null}
        <div className={`${styles.actions} ${styles.barreActions}`}>
          {rang > 0 ? (
            <Button
              type="button"
              variant="ghost"
              onClick={() => aller(ETAPES[rang - 1] ?? 'contenu')}
            >
              {t('regie:newsletter.etapes.precedent')}
            </Button>
          ) : null}
          {!lectureSeule ? (
            <Button type="submit" disabled={modifier.isPending || !sale}>
              {t('regie:newsletter.campagne.enregistrer')}
            </Button>
          ) : null}
          {rang < ETAPES.length - 1 ? (
            <Button
              type="button"
              variant="secondary"
              onClick={() => aller(ETAPES[rang + 1] ?? 'envoi')}
            >
              {t('regie:newsletter.etapes.suivant')}
            </Button>
          ) : null}
        </div>
      </form>
      <EnvoiDialog
        campagne={initiale}
        destinataires={destinataires}
        expediteur={expediteur}
        open={envoiOuvert}
        onOpenChange={setEnvoi}
      />
    </div>
  );
}
